import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider, signOut, deleteUser } from 'firebase/auth';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';

function useIsMobile() {
  const [v, setV] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const h = () => setV(window.innerWidth < 640);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return v;
}

function getAvatarColor(str) {
  const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#16a34a', '#0891b2', '#dc2626'];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

export default function Settings() {
  const { currentUser, userRole, orgId, orgName } = useAuth();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('profile');

  const [displayName, setDisplayName]       = useState(currentUser?.displayName || '');
  const [profileSaving, setProfileSaving]   = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError]     = useState('');

  const [currentPassword, setCurrentPassword]   = useState('');
  const [newPassword, setNewPassword]           = useState('');
  const [confirmPassword, setConfirmPassword]   = useState('');
  const [securitySaving, setSecuritySaving]     = useState(false);
  const [securitySuccess, setSecuritySuccess]   = useState('');
  const [securityError, setSecurityError]       = useState('');

  const [newOrgName, setNewOrgName] = useState(orgName || '');
  const [orgSaving, setOrgSaving]   = useState(false);
  const [orgSuccess, setOrgSuccess] = useState('');
  const [orgError, setOrgError]     = useState('');

  const avatarColor  = getAvatarColor(currentUser?.email || '');
  const avatarLetter = (currentUser?.displayName || currentUser?.email || '?')[0].toUpperCase();

  const handleSaveProfile = async () => {
    if (!displayName.trim()) { setProfileError('Name cannot be empty.'); return; }
    setProfileSaving(true); setProfileError(''); setProfileSuccess('');
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), { name: displayName.trim() });
      setProfileSuccess('Profile updated successfully.');
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch { setProfileError('Failed to update profile.'); }
    finally { setProfileSaving(false); }
  };

  const handleChangePassword = async () => {
    setSecurityError(''); setSecuritySuccess('');
    if (!currentPassword || !newPassword || !confirmPassword) { setSecurityError('All fields are required.'); return; }
    if (newPassword.length < 6) { setSecurityError('New password must be at least 6 characters.'); return; }
    if (newPassword !== confirmPassword) { setSecurityError('New passwords do not match.'); return; }
    setSecuritySaving(true);
    try {
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
      setSecuritySuccess('Password changed successfully.');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      setTimeout(() => setSecuritySuccess(''), 3000);
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential')
        setSecurityError('Current password is incorrect.');
      else setSecurityError('Failed to change password. Try again.');
    } finally { setSecuritySaving(false); }
  };

  const handleSaveOrg = async () => {
    if (!newOrgName.trim()) { setOrgError('Business name cannot be empty.'); return; }
    setOrgSaving(true); setOrgError(''); setOrgSuccess('');
    try {
      await updateDoc(doc(db, 'organizations', orgId), { name: newOrgName.trim() });
      setOrgSuccess('Business name updated.');
      setTimeout(() => setOrgSuccess(''), 3000);
    } catch { setOrgError('Failed to update business name.'); }
    finally { setOrgSaving(false); }
  };

  const tabs = [
    { key: 'profile',      label: '👤 Profile' },
    { key: 'security',     label: '🔒 Security' },
    ...(userRole === 'admin' ? [{ key: 'organization', label: '🏢 Organization' }] : []),
    { key: 'account',      label: '⚙️ Account' },
  ];

  const inputStyle = { ...styles.input, fontSize: '16px' /* prevents iOS auto-zoom */ };

  return (
    <div>
      <div style={styles.header}>
        <h1 style={{ ...styles.title, fontSize: isMobile ? '20px' : '24px' }}>Settings</h1>
        <p style={styles.subtitle}>Manage your account and preferences</p>
      </div>

      {/* Mobile: horizontal scrollable tab bar / Desktop: sidebar */}
      {isMobile ? (
        <div style={m.tabBar}>
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              style={activeTab === t.key ? m.tabActive : m.tab}
            >
              {t.label}
            </button>
          ))}
        </div>
      ) : (
        <div style={styles.layout}>
          <div style={styles.tabSidebar}>
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                style={activeTab === t.key ? styles.tabItemActive : styles.tabItem}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div style={styles.content}>
            <TabContent
              activeTab={activeTab} isMobile={false}
              inputStyle={inputStyle}
              displayName={displayName} setDisplayName={setDisplayName}
              profileSaving={profileSaving} profileSuccess={profileSuccess} profileError={profileError}
              handleSaveProfile={handleSaveProfile}
              currentUser={currentUser} userRole={userRole} orgName={orgName}
              avatarColor={avatarColor} avatarLetter={avatarLetter}
              currentPassword={currentPassword} setCurrentPassword={setCurrentPassword}
              newPassword={newPassword} setNewPassword={setNewPassword}
              confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword}
              securitySaving={securitySaving} securitySuccess={securitySuccess} securityError={securityError}
              handleChangePassword={handleChangePassword}
              newOrgName={newOrgName} setNewOrgName={setNewOrgName}
              orgSaving={orgSaving} orgSuccess={orgSuccess} orgError={orgError}
              handleSaveOrg={handleSaveOrg} orgId={orgId}
            />
          </div>
        </div>
      )}

      {isMobile && (
        <div style={{ padding: '0 0 24px' }}>
          <TabContent
            activeTab={activeTab} isMobile={true}
            inputStyle={inputStyle}
            displayName={displayName} setDisplayName={setDisplayName}
            profileSaving={profileSaving} profileSuccess={profileSuccess} profileError={profileError}
            handleSaveProfile={handleSaveProfile}
            currentUser={currentUser} userRole={userRole} orgName={orgName}
            avatarColor={avatarColor} avatarLetter={avatarLetter}
            currentPassword={currentPassword} setCurrentPassword={setCurrentPassword}
            newPassword={newPassword} setNewPassword={setNewPassword}
            confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword}
            securitySaving={securitySaving} securitySuccess={securitySuccess} securityError={securityError}
            handleChangePassword={handleChangePassword}
            newOrgName={newOrgName} setNewOrgName={setNewOrgName}
            orgSaving={orgSaving} orgSuccess={orgSuccess} orgError={orgError}
            handleSaveOrg={handleSaveOrg} orgId={orgId}
          />
        </div>
      )}
    </div>
  );
}

// Extracted so mobile and desktop share the same panel content
function TabContent({ activeTab, isMobile, inputStyle, displayName, setDisplayName,
  profileSaving, profileSuccess, profileError, handleSaveProfile,
  currentUser, userRole, orgName, avatarColor, avatarLetter,
  currentPassword, setCurrentPassword, newPassword, setNewPassword,
  confirmPassword, setConfirmPassword, securitySaving, securitySuccess,
  securityError, handleChangePassword, newOrgName, setNewOrgName,
  orgSaving, orgSuccess, orgError, handleSaveOrg, orgId }) {

  const card = { ...styles.card, padding: isMobile ? '16px' : '28px' };
  const btn  = { ...styles.btn,  width: isMobile ? '100%' : 'auto' };
  const btnD = { ...styles.btnDisabled, width: isMobile ? '100%' : 'auto' };

  return (
    <>
      {activeTab === 'profile' && (
        <div style={card}>
          <h2 style={styles.cardTitle}>Profile</h2>
          <p style={styles.cardSubtitle}>Update your display name</p>

          {profileError   && <div style={styles.error}>{profileError}</div>}
          {profileSuccess && <div style={styles.success}>{profileSuccess}</div>}

          <div style={{ ...styles.avatarSection, padding: isMobile ? '12px' : '16px' }}>
            <div style={{ ...styles.avatar, backgroundColor: avatarColor }}>{avatarLetter}</div>
            <div>
              <p style={styles.avatarName}>{displayName || currentUser?.email}</p>
              <p style={styles.avatarRole}>{userRole} · {orgName}</p>
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Display Name</label>
            <input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Your name" style={inputStyle} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Email Address</label>
            <input type="email" value={currentUser?.email} style={{ ...inputStyle, backgroundColor: '#f1f5f9', color: '#94a3b8' }} disabled />
            <p style={styles.fieldHint}>Email cannot be changed</p>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Role</label>
            <input type="text" value={userRole} style={{ ...inputStyle, backgroundColor: '#f1f5f9', color: '#94a3b8', textTransform: 'capitalize' }} disabled />
          </div>
          <button onClick={handleSaveProfile} disabled={profileSaving} style={profileSaving ? btnD : btn}>
            {profileSaving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      )}

      {activeTab === 'security' && (
        <div style={card}>
          <h2 style={styles.cardTitle}>Change Password</h2>
          <p style={styles.cardSubtitle}>Use a strong password you don't use elsewhere</p>

          {securityError   && <div style={styles.error}>{securityError}</div>}
          {securitySuccess && <div style={styles.success}>{securitySuccess}</div>}

          {[
            { label: 'Current Password',     value: currentPassword, set: setCurrentPassword, placeholder: 'Enter current password' },
            { label: 'New Password',          value: newPassword,     set: setNewPassword,     placeholder: 'Min 6 characters' },
            { label: 'Confirm New Password',  value: confirmPassword, set: setConfirmPassword, placeholder: 'Repeat new password' },
          ].map(field => (
            <div key={field.label} style={styles.field}>
              <label style={styles.label}>{field.label}</label>
              <input type="password" value={field.value} onChange={e => field.set(e.target.value)} placeholder={field.placeholder} style={inputStyle} />
            </div>
          ))}
          <button onClick={handleChangePassword} disabled={securitySaving} style={securitySaving ? btnD : btn}>
            {securitySaving ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      )}

      {activeTab === 'organization' && userRole === 'admin' && (
        <div style={card}>
          <h2 style={styles.cardTitle}>Organization</h2>
          <p style={styles.cardSubtitle}>Manage your business details</p>

          {orgError   && <div style={styles.error}>{orgError}</div>}
          {orgSuccess && <div style={styles.success}>{orgSuccess}</div>}

          <div style={styles.field}>
            <label style={styles.label}>Business Name</label>
            <input type="text" value={newOrgName} onChange={e => setNewOrgName(e.target.value)} placeholder="Your business name" style={inputStyle} />
          </div>
          <button onClick={handleSaveOrg} disabled={orgSaving} style={orgSaving ? btnD : btn}>
            {orgSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      {activeTab === 'account' && (
        <div>
          <div style={{ ...card, marginBottom: '16px' }}>
            <h2 style={styles.cardTitle}>Sign Out</h2>
            <p style={styles.cardSubtitle}>Sign out of your StockSpace account on this device</p>
            <button onClick={() => signOut(auth)} style={{ ...styles.signOutBtn, width: isMobile ? '100%' : 'auto' }}>
              Sign Out
            </button>
          </div>
          <div style={{ ...card, borderColor: '#fecaca' }}>
            <h2 style={{ ...styles.cardTitle, color: '#ef4444' }}>Delete Account</h2>
            <p style={styles.cardSubtitle}>Permanently delete your account. This cannot be undone.</p>
            <DeleteAccount currentUser={currentUser} orgId={orgId} userRole={userRole} isMobile={isMobile} />
          </div>
        </div>
      )}
    </>
  );
}

function DeleteAccount({ currentUser, orgId, userRole, isMobile }) {
  const [step, setStep]         = useState('idle');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!password) { setError('Please enter your password to confirm.'); return; }
    setDeleting(true); setError('');
    try {
      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential);
      await deleteDoc(doc(db, 'users', currentUser.uid));
      if (userRole === 'admin') await deleteDoc(doc(db, 'organizations', orgId));
      await deleteUser(currentUser);
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential')
        setError('Incorrect password.');
      else setError('Failed to delete account. Try again.');
      setDeleting(false);
    }
  };

  if (step === 'idle') return (
    <div>
      <div style={dangerStyles.warningBox}>
        ⚠️ This will permanently delete your account and all associated data. If you are the admin, your entire organization's data will also be deleted.
      </div>
      <button onClick={() => setStep('confirm')} style={{ ...dangerStyles.deleteBtn, width: isMobile ? '100%' : 'auto' }}>
        Delete My Account
      </button>
    </div>
  );

  return (
    <div>
      <div style={dangerStyles.warningBox}>
        This action is <strong>irreversible</strong>. Enter your password to confirm.
      </div>
      {error && <div style={dangerStyles.error}>{error}</div>}
      <div style={{ marginBottom: '16px' }}>
        <label style={dangerStyles.label}>Confirm Password</label>
        <input
          type="password" value={password} onChange={e => setPassword(e.target.value)}
          placeholder="Enter your password"
          style={{ ...dangerStyles.input, fontSize: '16px' }}
        />
      </div>
      <div style={{ display: 'flex', gap: '10px', flexDirection: isMobile ? 'column' : 'row' }}>
        <button onClick={handleDelete} disabled={deleting}
          style={deleting ? dangerStyles.deleteBtnDisabled : { ...dangerStyles.deleteBtn, flex: isMobile ? 1 : 'unset' }}>
          {deleting ? 'Deleting...' : 'Confirm Delete'}
        </button>
        <button onClick={() => { setStep('idle'); setError(''); setPassword(''); }}
          style={{ ...dangerStyles.cancelBtn, flex: isMobile ? 1 : 'unset' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Mobile styles ─────────────────────────────────────────────────────────────
const m = {
  tabBar: {
    display: 'flex', overflowX: 'auto', gap: '6px',
    padding: '0 0 12px', marginBottom: '12px',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',           // Firefox
    msOverflowStyle: 'none',          // IE
  },
  tab: {
    padding: '8px 16px', border: '1.5px solid #e2e8f0',
    borderRadius: '20px', backgroundColor: '#ffffff',
    color: '#64748b', fontSize: '13px', fontWeight: '500',
    cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
  },
  tabActive: {
    padding: '8px 16px', border: '1.5px solid #3b82f6',
    borderRadius: '20px', backgroundColor: '#eff6ff',
    color: '#3b82f6', fontSize: '13px', fontWeight: '700',
    cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
  },
};

// ── Desktop styles (unchanged) ────────────────────────────────────────────────
const dangerStyles = {
  warningBox: { backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px 16px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px', lineHeight: 1.5 },
  error: { backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', backgroundColor: '#f8fafc', border: '1.5px solid #fecaca', borderRadius: '8px', color: '#1e293b', fontSize: '14px', boxSizing: 'border-box' },
  deleteBtn: { padding: '10px 20px', backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  deleteBtnDisabled: { padding: '10px 20px', backgroundColor: '#fca5a5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'not-allowed' },
  cancelBtn: { padding: '10px 20px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', color: '#64748b', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
};

const styles = {
  header: { marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' },
  subtitle: { fontSize: '14px', color: '#64748b' },
  layout: { display: 'flex', gap: '24px', alignItems: 'flex-start' },
  tabSidebar: { width: '200px', minWidth: '200px', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  tabItem: { width: '100%', padding: '10px 14px', border: 'none', backgroundColor: 'transparent', color: '#64748b', fontSize: '14px', cursor: 'pointer', textAlign: 'left', borderRadius: '8px', fontWeight: '500', transition: 'all 0.15s ease' },
  tabItemActive: { width: '100%', padding: '10px 14px', border: 'none', backgroundColor: '#eff6ff', color: '#3b82f6', fontSize: '14px', cursor: 'pointer', textAlign: 'left', borderRadius: '8px', fontWeight: '600' },
  content: { flex: 1 },
  card: { backgroundColor: '#ffffff', borderRadius: '12px', padding: '28px', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  cardTitle: { fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' },
  cardSubtitle: { fontSize: '14px', color: '#64748b', marginBottom: '24px' },
  avatarSection: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' },
  avatar: { width: '56px', height: '56px', borderRadius: '50%', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: '700', flexShrink: 0 },
  avatarName: { fontSize: '15px', fontWeight: '600', color: '#1e293b', marginBottom: '2px' },
  avatarRole: { fontSize: '12px', color: '#94a3b8', textTransform: 'capitalize' },
  field: { marginBottom: '18px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', backgroundColor: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', fontSize: '14px', boxSizing: 'border-box' },
  fieldHint: { fontSize: '12px', color: '#94a3b8', marginTop: '4px' },
  btn: { padding: '10px 24px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(59,130,246,0.3)' },
  btnDisabled: { padding: '10px 24px', backgroundColor: '#93c5fd', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'not-allowed' },
  error: { backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' },
  success: { backgroundColor: '#f0fdf4', border: '1px solid #86efac', color: '#16a34a', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' },
  signOutBtn: { padding: '10px 24px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', color: '#374151', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
};